import { Callout, HelpLink, Related } from "./parts";

export function Installations() {
  return (
    <>
      <p>
        An installation is one proxy deployed in a customer's AWS account. It has an <strong>id</strong>, a name, the AWS
        account and region it runs in, and a <strong>secret</strong> it uses to send usage to Nexus. A customer can have
        several, for example one per AWS account or region. They are listed on the customer's{" "}
        <strong>AWS accounts</strong> tab.
      </p>

      <h2>Adding one</h2>
      <p>
        The <HelpLink to="installer" /> creates the installation for you as part of an install. To deploy the proxy by hand
        instead, register the installation on the AWS accounts tab first: give it a name (and optionally its AWS account),
        copy the secret it shows, and store it in the customer's Secrets Manager before deploying the proxy stack.
      </p>
      <Callout>
        The secret is shown <strong>once</strong>. If it is lost before the proxy is deployed, revoke the installation and
        register a new one.
      </Callout>
      <p>
        The installation id is also what developers use as the audience of their AWS sign-in token. See{" "}
        <HelpLink to="users-teams-and-sign-in" section="aws-sign-in" />.
      </p>

      <h2 id="health">Health</h2>
      <p>Each proxy sends a heartbeat every minute with its version and queue depth. The list shows:</p>
      <table>
        <thead>
          <tr>
            <th>Health</th>
            <th>Means</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Healthy</td>
            <td>A heartbeat arrived in the last 15 minutes.</td>
          </tr>
          <tr>
            <td>Stale</td>
            <td>
              The last heartbeat is older than 15 minutes. The proxy may be down, or unable to reach Nexus; its usage waits
              in its queue until it can.
            </td>
          </tr>
          <tr>
            <td>Unknown</td>
            <td>No heartbeat ever arrived: the proxy was never deployed, or never reached Nexus.</td>
          </tr>
          <tr>
            <td>Revoked</td>
            <td>The installation's secrets no longer work.</td>
          </tr>
        </tbody>
      </table>

      <h2 id="secrets">Secrets and rotation</h2>
      <p>Rotating the secret takes two steps, so the proxy never stops sending usage:</p>
      <ol>
        <li>
          <strong>Rotate</strong> issues a second secret. Both the old and the new one work. Put the new one in the
          customer's Secrets Manager and let the proxy pick it up.
        </li>
        <li>
          <strong>Promote</strong> makes the new secret the only one. The old one stops working.
        </li>
      </ol>
      <p>
        Only one rotation can be in progress at a time. <strong>Revoke</strong> stops both secrets at once; the
        installation and the usage it recorded are kept. It cannot be undone.
      </p>

      <h2 id="billing-attribution-in-aws-billing">Billing attribution in AWS Billing</h2>
      <p>
        Each proxy signs a person's Bedrock calls with that person's own AWS session, named and tagged with their{" "}
        <strong>user</strong>, <strong>team</strong> and, for AWS sign-in, <strong>aws_principal</strong>. The customer's
        AWS Billing can then split the Bedrock bill by person and team. The <strong>Billing attribution</strong> card at
        the top of a customer's Usage tab shows how much of the last 24 hours' calls were billed that way.
      </p>
      <ul>
        <li>
          The large figure is the share of calls made for a person that AWS bills to that person. Calls by service
          credentials, from an installation with attribution turned off, or from proxies too old to report are left out.
        </li>
        <li>
          At 99 % or more the card says so in one line. Below that, each reason is a line with what to do. "AWS refused to
          start a person's session" means the Bedrock role's trust policy does not allow <code>sts:TagSession</code> for
          the proxy; the customer's AWS administrator fixes the role.
        </li>
        <li>The table names each installation with its calls, its share and its most common reason.</li>
      </ul>
      <p>
        For the split to appear in the customer's bill, someone in their <strong>payer account</strong> activates the{" "}
        <code>user</code>, <code>team</code> and <code>aws_principal</code> cost allocation tags (type IAM principal) and
        creates a new CUR 2.0 export with the caller identity included. The proxy install guide's "Billing attribution in
        AWS Billing" gives the steps and the trust policy for a cross-account role.
      </p>
      <Callout>
        Nexus bills every call the same way whichever role signed it. Attribution changes only how the customer's own AWS
        bill is split.
      </Callout>

      <Related to={["installer", "users-teams-and-sign-in", "reconciliation"]} />
    </>
  );
}
